import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditTransferInvoiceDialog from '../components/invoices/EditTransferInvoiceDialog';
import api from '../services/api';
import { getKsefInvoice } from '../services/ksefService';

/**
 * Strona edycji faktury otwierana w nowej karcie.
 *
 * Źródła danych (z priorytetem):
 *  1. `sessionStorage.edit-invoice-<id>` — szybki start (okno-rodzic zapisuje
 *     obiekt faktury przed `window.open`). UWAGA: w nowej karcie Chromium
 *     sessionStorage często NIE jest klonowany z rodzica (bug w WebKit/Chromium),
 *     więc nie możemy na tym polegać.
 *  2. Backend:
 *     - ID liczbowe → GET /transfer-invoices i wybór po id.
 *     - ID niebędące liczbą (= ksef_invoice_id) → GET /ksef/invoices/:id
 *       i mapowanie na format faktury przelewowej.
 *  3. Gdy żadne ze źródeł nie dostarczy danych, pokazujemy czytelny komunikat
 *     błędu zamiast wiecznego „Ładowanie…”.
 */
const InvoiceEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = React.useState<any>(null);
  const [highlightWarehouse, setHighlightWarehouse] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const isNumericId = React.useMemo(() => !!id && !isNaN(Number(id)), [id]);

  React.useEffect(() => {
    let cancelled = false;

    let sessionData: any = null;
    try {
      const key = `edit-invoice-${id}`;
      const data = sessionStorage.getItem(key);
      if (data) {
        sessionData = JSON.parse(data);
        const { highlightWarehouse: hw, ...invoiceData } = sessionData;
        setHighlightWarehouse(Boolean(hw));
        setInvoice(invoiceData);
      }
    } catch (e) {
      console.warn('Nie udało się odczytać danych faktury z sessionStorage:', e);
    }

    if (!id) return () => { cancelled = true; };

    (async () => {
      try {
        if (isNumericId) {
          const { data: resp } = await api.get('/transfer-invoices');
          const rows = resp?.data || [];
          const fresh = rows.find((r: any) => String(r.id) === String(id));
          if (cancelled) return;
          if (fresh) {
            setInvoice((prev: any) => {
              if (!prev) return fresh;
              const merged = { ...fresh, ...prev };
              if (!prev.contractor_address && fresh.contractor_address) merged.contractor_address = fresh.contractor_address;
              if (!prev.contractor_postal_city && fresh.contractor_postal_city) merged.contractor_postal_city = fresh.contractor_postal_city;
              if (!prev.ksef_number && fresh.ksef_number) merged.ksef_number = fresh.ksef_number;
              if (!prev.account_number && fresh.account_number) merged.account_number = fresh.account_number;
              return merged;
            });
          } else if (!sessionData) {
            setLoadError(`Nie znaleziono faktury o id=${id}. Sprawdź, czy nadal istnieje.`);
          }
          return;
        }

        const details = await getKsefInvoice(id);
        if (cancelled) return;
        const ksefInv: any = details?.invoice;
        if (!ksefInv) {
          if (!sessionData) setLoadError('Nie udało się pobrać danych faktury z KSeF.');
          return;
        }

        // Numer rachunku: preferuj pole z nagłówka; jeśli puste, weź rachunek
        // oznaczony jako `is_primary` z listy `bankAccounts` zwracanej przez
        // backend (a nie pierwszy lepszy – mógłby trafić rachunek VAT lub
        // konto własne banku, które backend już prawidłowo odsiewa).
        const bankAccountsList: Array<any> = Array.isArray((details as any)?.bankAccounts)
          ? (details as any).bankAccounts
          : [];
        const primaryAcc =
          bankAccountsList.find((a) => (a?.is_primary === 1 || a?.is_primary === true) && a?.nr_rb)?.nr_rb ||
          bankAccountsList.find((a) => a?.nr_rb)?.nr_rb ||
          '';
        const accountNumber = ksefInv.bank_account_number || primaryAcc || '';

        // MySQL DATE serializuje się do ISO `2026-04-24T00:00:00.000Z`, a HTML
        // <input type="date"> akceptuje wyłącznie `YYYY-MM-DD`. Normalizujemy.
        const toIsoDate = (v: any): string => {
          if (!v) return '';
          const s = String(v);
          const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return `${m[1]}-${m[2]}-${m[3]}`;
          const d = new Date(s);
          if (!isNaN(d.getTime())) {
            const pad = (n: number) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          }
          return '';
        };

        const issueDate = toIsoDate(ksefInv.issue_date);
        const invoicingDate = toIsoDate(ksefInv.invoicing_date);
        const dueDate = toIsoDate(ksefInv.payment_due_date);

        // Backend zapisuje `seller_address` jako listę fragmentów adresu
        // złączoną przecinkami (AdresL1/L2 LUB Ulica+KodPocztowy+Miejscowosc).
        // Rozbijamy na dwa pola formularza: "ulica i numer" + "kod pocztowy
        // i miejscowość" heurystycznie po obecności kodu pocztowego (99-999).
        const splitAddress = (raw: any): { line1: string; postalCity: string } => {
          if (!raw) return { line1: '', postalCity: '' };
          const parts = String(raw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          if (parts.length === 0) return { line1: '', postalCity: '' };
          if (parts.length === 1) return { line1: parts[0], postalCity: '' };
          const postalRe = /\b\d{2}-\d{3}\b/;
          const postalIdx = parts.findIndex((p) => postalRe.test(p));
          if (postalIdx >= 0) {
            const line1 = parts.slice(0, postalIdx).join(', ').trim();
            const postalCity = parts.slice(postalIdx).join(', ').trim();
            return { line1, postalCity };
          }
          return {
            line1: parts.slice(0, -1).join(', ').trim(),
            postalCity: parts[parts.length - 1],
          };
        };

        const sellerAddr = splitAddress(ksefInv.seller_address);

        // Sposób płatności z KSeF musi trafić do `document_type`, żeby dialog
        // nie nadpisał go domyślnym „Przelew".
        const mapKsefPaymentToDocType = (m: any): 'Przelew' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)' | 'Proforma' => {
          if (!m) return 'Przelew';
          const s = String(m).toLowerCase().trim();
          if (s === 'gotówka' || s === 'gotowka' || s === 'cash') return 'Gotówka';
          if (s === 'karta' || s === 'card') return 'Karta';
          if (s.includes('zaliczka')) return 'Zaliczka (przelew)';
          if (s === 'proforma') return 'Proforma';
          return 'Przelew';
        };

        const mapped: any = {
          ...ksefInv,
          id: ksefInv.ksef_invoice_id || id,
          ksef_invoice_id: ksefInv.ksef_invoice_id || id,
          ksef_number: ksefInv.ksef_invoice_id || id,
          ksef_status: ksefInv.status || 'Do weryfikacji',
          document_type: mapKsefPaymentToDocType(ksefInv.payment_method),
          document_kind: ksefInv.invoice_type === 'correction' ? 'correction' : 'vat',
          document_date: issueDate,
          received_date: invoicingDate,
          contractor_name: ksefInv.seller_name || '',
          contractor_address: sellerAddr.line1,
          contractor_postal_city: sellerAddr.postalCity,
          nip: ksefInv.seller_tax_id || '',
          account_number: accountNumber,
          net_amount: Number(ksefInv.net_amount || 0),
          gross_amount: Number(ksefInv.gross_amount || 0),
          due_date: dueDate,
          invoice_number: ksefInv.invoice_number || '',
          payment_method: ksefInv.payment_method || null,
          payment_due_date: dueDate || null,
        };
        setInvoice((prev: any) => {
          if (!prev) return mapped;
          const merged = { ...mapped, ...prev };
          // Dla KSeF preferujemy świeże dane z API nad danymi z sessionStorage
          // (w liście faktur `document_type`, `account_number` i daty bywają
          // błędne lub w niewłaściwym formacie – ISO zamiast YYYY-MM-DD).
          merged.document_type = mapped.document_type;
          merged.payment_method = mapped.payment_method;
          merged.account_number = mapped.account_number;
          merged.document_date = mapped.document_date || prev.document_date || '';
          merged.received_date = mapped.received_date || prev.received_date || '';
          merged.due_date = mapped.due_date || prev.due_date || '';
          merged.payment_due_date = mapped.payment_due_date || prev.payment_due_date || null;
          merged.contractor_address = mapped.contractor_address || prev.contractor_address || '';
          merged.contractor_postal_city = mapped.contractor_postal_city || prev.contractor_postal_city || '';
          return merged;
        });
      } catch (e: any) {
        console.error('Nie udało się pobrać danych faktury:', e);
        if (!sessionData && !cancelled) {
          const status = e?.response?.status;
          const msg = e?.response?.data?.message || e?.message || 'Nieznany błąd';
          if (status === 401) {
            setLoadError('Brak aktywnej sesji KSeF. Zaloguj się ponownie do KSeF w ustawieniach.');
          } else if (status === 429) {
            setLoadError('KSeF ograniczył liczbę zapytań (429). Spróbuj ponownie za chwilę.');
          } else {
            setLoadError(`Nie udało się pobrać danych faktury: ${msg}`);
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, [id, isNumericId]);

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/accounting/purchases');
    }
  };

  const handleSaved = () => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'invoice:updated' }, '*');
      }
    } catch {}
    handleClose();
  };

  if (!invoice) {
    if (loadError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, fontFamily: 'Roboto, sans-serif', padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#c62828' }}>Nie udało się wczytać faktury</div>
          <div style={{ color: '#555', maxWidth: 520 }}>{loadError}</div>
          <button onClick={() => window.close()} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Zamknij okno</button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Roboto, sans-serif' }}>
        Ładowanie danych faktury...
      </div>
    );
  }

  return (
    <EditTransferInvoiceDialog
      open={true}
      onClose={handleClose}
      invoice={invoice}
      onSaved={handleSaved}
      highlightWarehouse={highlightWarehouse}
    />
  );
};

export default InvoiceEditPage;
